import { legacyRedirect, type Organization } from "@nodus/api";
import { Button, Callout } from "@nodus/ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { AuthCard } from "../../app/auth-card";
import { homePath } from "../../app/home";
import { lastScope, rememberScope } from "../../app/scope";

// `/` resolves legacy deep links first, then lands on the last-used org and project (ADR-107); a first run goes to
// onboarding. A legacy link with several orgs and no last-used one asks which org, then continues to the target.
export const Route = createFileRoute("/")({
  validateSearch: (s: Record<string, unknown>): { notice?: string } => (typeof s.notice === "string" ? { notice: s.notice } : {}),
  loader: async ({ context, location }) => {
    const url = new URL(`${window.location.origin}${location.href}${window.location.hash}`);
    const legacy = legacyRedirect(url);
    if (legacy && !legacy.needsOrg) throw redirect({ href: legacy.to("", "") });
    const session = await context.auth.getSession();
    if (!session) throw redirect({ to: "/sign-in", search: { redirect: location.href } });
    const orgs = await context.queryClient.fetchQuery({
      queryKey: ["user", "orgs", session.user.id],
      queryFn: context.api.organizations,
    });
    if (orgs.length === 0) throw redirect({ to: "/onboarding" });
    const last = lastScope(session.user.id);
    const known = last.org && orgs.some((o) => o.metadata.name === last.org) ? last.org : undefined;
    const notice = (location.search as { notice?: string }).notice;
    if (legacy && !known && orgs.length > 1) return { choose: orgs, target: url.href, userId: session.user.id, notice };
    if (notice === "left-org") return { choose: orgs, target: undefined, userId: session.user.id, notice };
    const org = known ?? orgs[0]!.metadata.name;
    throw redirect({ href: legacy ? legacy.to(org, last.project(org)) : homePath(org, last.project(org)) });
  },
  component: ChooseOrg,
});

function ChooseOrg() {
  const { choose, target, userId, notice } = Route.useLoaderData() as { choose: Organization[]; target?: string; userId: string; notice?: string };
  const navigate = useNavigate();
  function pick(org: string) {
    rememberScope(userId, org);
    const legacy = target ? legacyRedirect(new URL(target)) : undefined;
    void navigate({ href: legacy ? legacy.to(org, "default") : homePath(org, lastScope(userId).project(org)) });
  }
  return (
    <AuthCard title="Choose an organization" description={target ? "This link is from the previous console. Pick the organization it belongs to." : undefined}>
      {notice === "left-org" ? <Callout tone="neutral" className="mb-4">You left the organization.</Callout> : null}
      <div className="grid gap-2">
        {choose.map((o) => (
          <Button key={o.metadata.name} variant="outline" onClick={() => pick(o.metadata.name)}>
            {o.spec?.displayName ?? o.metadata.name}
          </Button>
        ))}
      </div>
    </AuthCard>
  );
}
